/* eslint-disable react/require-default-props */
import React, { useEffect, useRef, useState } from 'react';
import { useDetectScroll, useResponsiveValueState } from '@saviynt/common';
import {
  Box,
  Button,
  FooterActionBar,
  Icon,
  InputField,
  ModalIsland,
  Switch,
  Tooltip,
  Typography,
} from '@saviynt/design-system';

import type { RoleAction } from '../../../services';
import type { InvertedLocalize } from '../../../utils/localization';

import { useUserDisplayName } from '../../../shared/hooks';
import { useInvertedLocalize } from '../../../utils/localization';

import invertedMessages, { type ActivateRoleModalMessage } from './messages';

import './ActivateRoleModal.css';

interface ActivateRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (formData: ActivateRoleFormData) => void;
  /** Callback when Save & Activate button is clicked */
  onSaveAndActivate: (
    formData: ActivateRoleFormData,
    actions: RoleAction[]
  ) => void;
  isSaving: boolean;
  isActivating: boolean;
  actions?: RoleAction[]; // Role actions/entitlements from the role data
  errorMessage?: string; // Error message to display under role name input
  onClearError?: () => void;
  defaultDescription?: string; // default desc icu string
}

export interface ActivateRoleFormData {
  roleName: string;
  description: string;
  isRequestable: boolean;
}

export type LocalizeFn = InvertedLocalize<ActivateRoleModalMessage>;

function ActivateRoleModal({
  isOpen,
  onClose,
  onSave,
  onSaveAndActivate,
  isSaving = false,
  isActivating = false,
  actions = [],
  errorMessage = '',
  onClearError,
  defaultDescription = '',
}: ActivateRoleModalProps): JSX.Element {
  const [roleName, setRoleName] = useState('');
  const [description, setDescription] = useState('');
  const [isAdvancedOptionsExpanded, setIsAdvancedOptionsExpanded] =
    useState(false);

  // Advanced options state
  const [isRequestable, setIsRequestable] = useState(true);

  const l = useInvertedLocalize(invertedMessages);

  const userDisplayName = useUserDisplayName();

  // set default description when modal opens
  useEffect(() => {
    if (isOpen && defaultDescription) {
      setDescription(defaultDescription);
    }
  }, [isOpen, defaultDescription]);

  // Responsive and scroll detection setup
  const [mediaQueryValue, setMediaQueryValue] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  const SCREEN_WIDTH_QUERIES = [
    { query: '(max-width: 599px)', value: 110, label: 'MobileXLarge' },
  ];

  useResponsiveValueState(setMediaQueryValue, SCREEN_WIDTH_QUERIES);
  const mobileXlargeQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === 'MobileXLarge'
  );
  const isMobileXlargeWidth =
    (mobileXlargeQuery?.value ?? 0) <= mediaQueryValue;

  const { isScrolled: sectionRefIsScrolled } = useDetectScroll(
    null,
    sectionRef
  );

  // Form validation - buttons enabled only when both name and description are filled
  const isFormValid = roleName.trim() !== '' && description.trim() !== '';

  // Reset form when modal closes
  const handleClose = () => {
    setRoleName('');
    setDescription('');
    setIsAdvancedOptionsExpanded(false);
    setIsRequestable(true);
    onClose();
  };

  // Handle save actions
  const handleSave = () => {
    if (!isFormValid) {
      return;
    }

    const formData: ActivateRoleFormData = {
      roleName: roleName.trim(),
      description: description.trim(),
      isRequestable,
    };

    onSave(formData);
  };

  const handleSaveAndActivate = () => {
    if (!isFormValid) {
      return;
    }

    const formData: ActivateRoleFormData = {
      roleName: roleName.trim(),
      description: description.trim(),
      isRequestable,
    };

    onSaveAndActivate(formData, actions);
  };

  // Toggle advanced options
  const toggleAdvancedOptions = () => {
    setIsAdvancedOptionsExpanded(!isAdvancedOptionsExpanded);
  };

  // Handle keyboard events for advanced options toggle
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleAdvancedOptions();
    }
  };

  // Modal content body
  const contentBody = (
    <div className='Roles-ActivateRoleModal-content' ref={sectionRef}>
      <Typography
        kind='body2'
        color='--color-foreground-neutral-medium'
        className='Roles-ActivateRoleModal-description'>
        {l('Please provide the details below to save and activate the role')}
      </Typography>

      {/* Form fields */}
      <Box className='Roles-ActivateRoleModal-form'>
        <InputField
          isRequired
          maxLength={250}
          name='roleName'
          label={
            <Typography kind='label' htmlFor='roleName'>
              {l('Role Name')}
            </Typography>
          }
          placeholder={l('Give this role a unique name')}
          value={roleName}
          setValue={(value: string) => {
            setRoleName(value);

            if (errorMessage && onClearError) {
              onClearError();
            }
          }}
          isCritical={!!errorMessage}
          CriticalHelperText={errorMessage}
        />

        <InputField
          isRequired
          maxLength={2000}
          name='description'
          label={
            <Typography kind='label' htmlFor='description'>
              {l('Description')}
            </Typography>
          }
          placeholder={l('Provide a brief description for your role')}
          value={description}
          setValue={setDescription}
        />

        <InputField
          name='owner'
          label={
            <Typography kind='label' htmlFor='owner'>
              {l('Owner')}
            </Typography>
          }
          placeholder=''
          value={userDisplayName}
          isReadOnly
          isDisabled
        />
      </Box>

      {/* Advanced Options */}
      <Box className='Roles-ActivateRoleModal-advancedOptions'>
        <div
          className='Roles-ActivateRoleModal-advancedOptionsHeader'
          onClick={toggleAdvancedOptions}
          role='button'
          tabIndex={0}
          onKeyDown={handleKeyDown}
          aria-expanded={isAdvancedOptionsExpanded}>
          <Icon
            kind='ChevronDown'
            rotate={isAdvancedOptionsExpanded ? '180' : '0'}
            size='medium'
            color='--color-foreground-neutral-medium'
          />
          <Typography kind='h3'>{l('Advanced Options')}</Typography>
        </div>

        {/* Advanced Options Content */}
        <Box
          className={`Roles-ActivateRoleModal-advancedOptionsContent ${
            isAdvancedOptionsExpanded
              ? 'Roles-ActivateRoleModal-advancedOptionsContent--isVisible'
              : 'Roles-ActivateRoleModal-advancedOptionsContent--isHidden'
          }`}
          aria-hidden={!isAdvancedOptionsExpanded}>
          {/* Requestable Switch */}
          <Box className='Roles-ActivateRoleModal-switchRow'>
            <Switch
              isChecked={isRequestable}
              onChange={() => setIsRequestable(!isRequestable)}
              label={l('Requestable')}
              labelPosition='right'
              className='Roles-ActivateRoleModal-switch'
            />
            <Tooltip
              text={l('Enable this option to allow users to request this role')}
              placement='top'>
              <Icon
                kind='AlertInfoFilled'
                size='medium'
                color='--color-foreground-info-subtle'
                className='Roles-ActivateRoleModal-infoIcon'
              />
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </div>
  );

  // Footer with action buttons
  const footerActionBar = (
    <Box className='Roles-ActivateRoleModal-footer'>
      <Button
        kind='outlined'
        size='medium'
        onClick={handleSave}
        isDisabled={!isFormValid || isSaving || isActivating}
        isLoading={isSaving}
        className='Roles-ActivateRoleModal-saveButton'>
        {l('Save')}
      </Button>
      <Button
        kind='filled'
        leftIcon={<Icon kind='QuickAction' />}
        size='medium'
        onClick={handleSaveAndActivate}
        isDisabled={!isFormValid || isSaving || isActivating}
        isLoading={isActivating}
        className='Roles-ActivateRoleModal-activateButton'>
        {l('Save & Activate')}
      </Button>
    </Box>
  );

  return (
    <ModalIsland
      isOpen={isOpen}
      onClose={handleClose}
      isMobileWidth={isMobileXlargeWidth}
      title={l('Save and Activate Role')}
      headerIcon={
        <Icon
          kind='QuickAction'
          color='--color-foreground-neutral-bold'
          size='medium'
        />
      }
      contentBody={contentBody}
      sectionRef={sectionRef}
      FooterActionBarComp={
        <FooterActionBar size='large' isScrolled={sectionRefIsScrolled}>
          {footerActionBar}
        </FooterActionBar>
      }
      className='Roles-ActivateRoleModal'
    />
  );
}

ActivateRoleModal.defaultProps = {
  actions: [],
};

export default ActivateRoleModal;
